import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import BackButton from "../components/BackButton";
import SEO from "../components/SEO";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Wallet as WalletIcon, TrendingUp, Clock, Download, PoundSterling,
  Receipt, ArrowUpRight, ShieldCheck, Sparkles,
} from "lucide-react";

const fmt = (n) => `£${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const monthKey = (iso) => {
  if (!iso) return "?";
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (key) => {
  const [y, m] = key.split("-");
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });
};

const EARN_TYPES = new Set(["payout", "partial_payout"]);

export default function CreatorEarnings() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/auth?mode=login"); return; }
    if (user.role !== "creator") { nav("/brand"); return; }
    api.get("/wallet/me").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [user, loading]);

  const {
    monthly, nextReleaseAt, upcomingCount, upcomingAmount, jobsCount, avgPerJob,
    earnings, brandsWorkedWith,
  } = useMemo(() => {
    if (!data) return { monthly: [], nextReleaseAt: null, upcomingCount: 0, upcomingAmount: 0, jobsCount: 0, avgPerJob: 0, earnings: [], brandsWorkedWith: 0 };
    const txns = data.transactions || [];

    // Monthly buckets (last 12 months incl. current)
    const now = new Date();
    const keys = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      keys.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    const bucket = Object.fromEntries(keys.map((k) => [k, 0]));
    for (const t of txns) {
      if (!EARN_TYPES.has(t.type)) continue;
      const k = monthKey(t.date);
      if (bucket[k] !== undefined) bucket[k] += Math.max(0, Number(t.amount || 0));
    }
    const monthly = keys.map((k) => ({ key: k, label: monthLabel(k), value: Math.round((bucket[k] || 0) * 100) / 100 }));

    // Upcoming (pending_payout)
    const pendingTxns = txns.filter((t) => t.type === "pending_payout");
    const upcomingCount = pendingTxns.length;
    const upcomingAmount = pendingTxns.reduce((a, t) => a + Number(t.amount || 0), 0);
    const dated = pendingTxns.map((t) => t.date).filter(Boolean).sort();
    const nextReleaseAt = dated[0] || null;

    // Earnings list
    const earnings = txns.filter((t) => EARN_TYPES.has(t.type));
    const jobsCount = earnings.length;
    const total = earnings.reduce((a, t) => a + Number(t.amount || 0), 0);
    const avgPerJob = jobsCount ? total / jobsCount : 0;
    const brandsWorkedWith = new Set(earnings.map((t) => t.counterparty).filter(Boolean)).size;

    return { monthly, nextReleaseAt, upcomingCount, upcomingAmount, jobsCount, avgPerJob, earnings, brandsWorkedWith };
  }, [data]);

  const download = async () => {
    setBusy(true);
    try {
      const rows = [["Receipt ID", "Date", "Type", "Status", "Brand", "Package", "Amount (£)"]];
      earnings.forEach((t) => rows.push([
        t.receipt_id || "", t.date || "", t.type, t.status, t.counterparty || "", t.title || "", Number(t.amount || 0).toFixed(2),
      ]));
      const csv = "\ufeff" + rows.map((r) => r.map((c) => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `nexus-earnings-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("CSV downloaded");
    } catch { toast.error("Download failed"); }
    finally { setBusy(false); }
  };

  if (!data) return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-6xl mx-auto p-10 text-zinc-500 animate-pulse">Loading earnings…</div>
    </div>
  );

  const peak = Math.max(1, ...monthly.map((m) => m.value));

  return (
    <div className="min-h-screen grain">
      <SEO title="Earnings · Nexus" description="Your creator earnings, upcoming payouts and monthly chart." path="/creator/earnings" />
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-6">
        <BackButton fallback="/creator" label="Back to dashboard" />
      </div>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-10 pt-4 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2 flex items-center gap-2">
              <PoundSterling className="h-3.5 w-3.5 text-nexus-pink" /> Earnings
            </div>
            <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter">Money earned on Nexus</h1>
            <p className="text-zinc-400 mt-1.5 text-sm">Everything you&apos;ve earned, everything queued up, and every fee taken — all in one place.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={download} disabled={busy || earnings.length === 0} className="nexus-gradient rounded-full text-white border-0 font-semibold hover:opacity-90" data-testid="earnings-download-csv">
              <Download className="h-4 w-4 mr-2" /> Download CSV
            </Button>
            <Button variant="outline" onClick={() => nav("/wallet")} className="rounded-full border-white/10 text-white hover:bg-white/5" data-testid="earnings-open-wallet">
              <WalletIcon className="h-4 w-4 mr-2" /> Full wallet
            </Button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="glass rounded-2xl p-5" data-testid="kpi-available">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><ShieldCheck className="h-3 w-3 text-emerald-400" /> Available</div>
            <div className="font-display font-bold text-3xl mt-2 tabular-nums text-emerald-300">{fmt(data.available_balance)}</div>
            <div className="text-[10px] text-zinc-500 mt-1">Withdraw any time</div>
          </div>
          <div className="glass rounded-2xl p-5" data-testid="kpi-pending">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Clock className="h-3 w-3 text-yellow-400" /> Pending clearance</div>
            <div className="font-display font-bold text-3xl mt-2 tabular-nums text-yellow-300">{fmt(data.pending_balance)}</div>
            <div className="text-[10px] text-zinc-500 mt-1">{upcomingCount} deal(s) in flight{nextReleaseAt ? ` · next since ${fmtDate(nextReleaseAt)}` : ""}</div>
          </div>
          <div className="glass rounded-2xl p-5" data-testid="kpi-lifetime">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><TrendingUp className="h-3 w-3 text-nexus-blue" /> Lifetime earned</div>
            <div className="font-display font-bold text-3xl mt-2 tabular-nums text-white">{fmt(data.lifetime_volume)}</div>
            <div className="text-[10px] text-zinc-500 mt-1">{jobsCount} completed job(s) · avg {fmt(avgPerJob)}</div>
          </div>
          <div className="glass rounded-2xl p-5" data-testid="kpi-fees">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Receipt className="h-3 w-3 text-nexus-pink" /> Platform fees paid</div>
            <div className="font-display font-bold text-3xl mt-2 tabular-nums text-nexus-pink">{fmt(data.total_creator_fees)}</div>
            <div className="text-[10px] text-zinc-500 mt-1">Creators keep 100% — no commission</div>
          </div>
        </div>

        {/* Monthly chart */}
        <div className="glass rounded-2xl p-6" data-testid="earnings-monthly-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Last 12 months</div>
              <div className="font-display font-bold text-xl">Monthly earnings</div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 hidden md:block">Values are your full payouts (no fees deducted)</div>
          </div>
          <div className="grid grid-cols-12 gap-2 h-40 items-end">
            {monthly.map((m) => {
              const h = Math.round((m.value / peak) * 100);
              return (
                <div key={m.key} className="flex flex-col items-center gap-1.5 justify-end" data-testid={`bar-${m.key}`}>
                  <div className="w-full relative group">
                    <div className="w-full rounded-t bg-gradient-to-t from-nexus-pink via-nexus-purple to-nexus-blue transition-all"
                         style={{ height: `${Math.max(2, h * 1.5)}px`, opacity: m.value === 0 ? 0.2 : 1 }} />
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none text-[10px] whitespace-nowrap bg-black/90 border border-white/10 px-2 py-1 rounded z-10">
                      {fmt(m.value)}
                    </div>
                  </div>
                  <div className="text-[9px] uppercase tracking-widest text-zinc-500">{m.label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upcoming payouts */}
        {upcomingCount > 0 && (
          <div className="glass rounded-2xl p-6" data-testid="earnings-upcoming-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Upcoming</div>
                <div className="font-display font-bold text-xl">Pending payouts</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Total pending</div>
                <div className="font-display font-bold text-2xl text-yellow-300 tabular-nums">{fmt(upcomingAmount)}</div>
              </div>
            </div>
            <div className="space-y-2">
              {(data.transactions || []).filter((t) => t.type === "pending_payout").slice(0, 8).map((t) => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] hairline" data-testid={`upcoming-${t.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{t.title}</div>
                    <div className="text-[11px] text-zinc-500 truncate">{t.counterparty} · funded {fmtDate(t.date)}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${t.status === "delivered" ? "bg-yellow-500/10 text-yellow-300 border-yellow-500/30" : t.status === "in_progress" ? "bg-nexus-blue/10 text-nexus-blue border-nexus-blue/30" : "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30"}`}>
                      {String(t.status).replace(/_/g, " ")}
                    </span>
                    <div className="font-display font-bold tabular-nums text-yellow-300 w-24 text-right">{fmt(t.amount)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Earnings ledger */}
        <div className="glass rounded-2xl p-6" data-testid="earnings-ledger">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Ledger</div>
              <div className="font-display font-bold text-xl">Every payout you&apos;ve earned</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Brands worked with</div>
              <div className="font-display font-bold text-2xl text-white tabular-nums">{brandsWorkedWith}</div>
            </div>
          </div>
          {earnings.length === 0 ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              <Sparkles className="h-6 w-6 mx-auto mb-2 text-zinc-600" />
              You haven&apos;t been paid out yet. Complete your first hire and it&apos;ll appear here.
            </div>
          ) : (
            <div className="space-y-2">
              {earnings.map((t) => (
                <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] hairline hover:bg-white/[0.04] transition-colors" data-testid={`earning-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{t.title}</div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {t.counterparty} · {fmtDate(t.date)} · <span className="font-mono">{t.receipt_id}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {t.type === "partial_payout" && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border bg-yellow-500/10 text-yellow-300 border-yellow-500/30">split</span>
                    )}
                    {t.status === "withdrawn" && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border bg-zinc-500/10 text-zinc-400 border-zinc-500/30">withdrawn</span>
                    )}
                    <div className="font-display font-bold tabular-nums text-emerald-300 w-24 text-right flex items-center justify-end gap-1">
                      <ArrowUpRight className="h-3.5 w-3.5" /> {fmt(t.amount)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <p className="text-xs text-zinc-500 text-center">Payouts are held in escrow until the brand approves the delivery. Creators keep 100% of the agreed price — Nexus charges its fee to the brand.</p>
      </div>
    </div>
  );
}
